import { parseCmsCasting } from '../../cmsCasting';
import type { Question } from '../../types';
import { resolveYesNoOptionPair } from '../primitives/yesNoRadio';

import vibecoderGuideMd from '../VIBECODER.md?raw';
import gatedYesNoTextRowExample from './examples/gated-yes-no-text-row.json?raw';
import layoutTypesTs from '../layout/types.ts?raw';
import { composeLayoutNodeVibecodeSection } from '../layout/vibecode/registry';
import { stripTypesForVibecode } from '../layout/vibecode/stripTypesForVibecode';

export type VibecodeVisitShell = 'oasis' | 'non_oasis' | 'unknown';

function visitShellFromCatalogIsOasis(
  isOasis: boolean | undefined
): VibecodeVisitShell {
  if (isOasis === undefined) {
    return 'unknown';
  }
  return isOasis ? 'oasis' : 'non_oasis';
}

function liteQuestionSnapshot(q: Question): Record<string, unknown> {
  const snap: Record<string, unknown> = {
    qid: q.qid,
    question: q.question,
    type: q.type,
  };
  if (q.description) snap.description = q.description;
  if (q.values) snap.values = q.values;
  if (q.type === 'radio' && resolveYesNoOptionPair(q.values) != null) {
    snap.yes_no_checkbox_eligible = true;
  }
  if (q.value_description) snap.value_description = q.value_description;
  const cms_casting = parseCmsCasting(q.cms_casting);
  if (cms_casting) snap.cms_casting = cms_casting;
  if (q.subitems) {
    const out: Record<string, unknown[]> = {};
    for (const [parentKey, sub] of Object.entries(q.subitems)) {
      out[parentKey] = sub.map(liteQuestionSnapshot);
    }
    snap.subitems = out;
  }
  return snap;
}

/**
 * Offline LLM paste bundle — section schema metadata + bespoke DSL.
 * Intended for admins only; excludes patient PHI.
 */
export function composeVibecodeLlmBundle(input: {
  visitTypeSummary: string;
  schemaVersionName: string;
  sectionName: string;
  questions: Question[];
  canonicalVisitTypeId?: string | null;
  isOasis?: boolean;
}): string {
  const shell = visitShellFromCatalogIsOasis(input.isOasis);
  const sectionJson = JSON.stringify(input.questions.map(liteQuestionSnapshot));
  const nodeVibecode = composeLayoutNodeVibecodeSection();
  const typesReference = stripTypesForVibecode(layoutTypesTs);

  const visitShellBlock =
    shell === 'non_oasis'
      ? [
          '## Visit shell (non-OASIS)',
          '- One layout for `section_name` **`All`** — all questions on one page.',
          '- No outer app Card/title; you group with layout nodes. Root `card` is fine.',
        ].join('\n')
      : shell === 'oasis'
        ? [
            '## Visit shell (OASIS)',
            '- One section per page; plain header above content. Root `card` optional.',
          ].join('\n')
        : '## Visit shell\n- `isOasis` unknown — use neutral grouping.';

  return [
    '# Bespoke layout_json context',
    '',
    'Use only QIDs from Section schema. Tailwind classes must be safelisted or they may not apply.',
    '',
    visitShellBlock,
    '',
    '## Routing',
    `- visit: ${input.visitTypeSummary}`,
    `- canonical_visit_type_id: "${(input.canonicalVisitTypeId ?? '').trim() || 'unknown'}"`,
    `- schema_version: "${input.schemaVersionName}"`,
    `- section_name: "${input.sectionName}"`,
    '',
    '## Section schema',
    'Render every question as a reachable answer control.',
    '```json',
    sectionJson,
    '```',
    '',
    vibecoderGuideMd.trimEnd(),
    '',
    nodeVibecode,
    '',
    '## DSL reference (`layout/types.ts`, comments stripped)',
    '```typescript',
    typesReference,
    '```',
    '',
    '## Example: `gated-yes-no-text-row`',
    'Yes/no gate + single-line subitem text. Checkbox stays outside `disabled-when`.',
    '```json',
    gatedYesNoTextRowExample.trimEnd(),
    '```',
    '',
    '## Constraints',
    '- Output: top-level **`LayoutNode[]`** JSON.',
    '- `type` discriminators and props from DSL reference only.',
    '- Every control **`qid`** must appear in Section schema.',
    '- Do not hide required controls with CSS (`hidden`, `invisible`, `opacity-0`, `display:none`, etc.).',
    '- Top-level schema QIDs (not under `subitems`) stay outside `conditional` / `disabled-when`.',
    '- No PHI or external URLs.',
  ]
    .filter(part => part !== '')
    .join('\n');
}
